import { LeftOutlined, RightOutlined } from "@ant-design/icons"
import { range } from "lodash"
import React, { useMemo } from "react"
import styled from "styled-components"
import Breakpoints from "../../components/shared/breakpoints"
import Link from "../atoms/link"
import { BT } from "../styles/blog-typography"

const PaginationContainer = styled.nav`
  display: flex;
  justify-content: center;
  gap: 8px;
`

const PaginationButton = styled(Link)<{ $highlighted?: boolean }>`
  ${BT.sm.info`400`};
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  width: 40px;
  height: 40px;
  color: ${(props) =>
    props.$highlighted ? `var(--burnt-orange)` : `var(--black)`};
  background-color: var(--white);
  border: 1px solid
    ${(props) => (props.$highlighted ? `var(--burnt-orange)` : `#d9d9d9`)};
  box-sizing: border-box;
  border-radius: 2px;
  cursor: pointer;
  :hover,
  :focus,
  :active {
    color: var(--burnt-orange);
    border: 1px solid var(--burnt-orange);
  }
  transition: all 0.2s ease-in-out;
  @media (min-width: ${Breakpoints.lg}) {
    width: 32px;
    height: 32px;
  }
`

const DisabledButton = styled.div`
  ${BT.sm.info`400`};
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  width: 40px;
  height: 40px;
  color: #d9d9d9;
  background-color: var(--white);
  border: 1px solid #d9d9d9;
  box-sizing: border-box;
  border-radius: 2px;
  cursor: not-allowed;
  @media (min-width: ${Breakpoints.lg}) {
    width: 32px;
    height: 32px;
  }
`

interface IPaginationProps {
  currentPage: number
  totalPages: number
  pagePrefix?: string
}

/** Page numbers with links to next and previous page */
const Pagination = ({
  currentPage,
  totalPages,
  pagePrefix,
}: IPaginationProps) => {
  // Get the range of page numbers to display
  const pageRange = useMemo(() => {
    const neighbouringPages = 2
    const maxPagesToShow = neighbouringPages * 2 + 1
    let start = 1
    let end = totalPages
    if (totalPages > maxPagesToShow) {
      // more pages than can be shown, determine the range
      if (currentPage <= neighbouringPages) {
        // current page is near the start
        start = 1
        end = maxPagesToShow
      } else if (currentPage + neighbouringPages >= totalPages) {
        // current page is near the end
        start = totalPages - (neighbouringPages * 2 + 1) + 1
        end = totalPages
      } else {
        // current page is somewhere in the middle
        start = currentPage - neighbouringPages
        end = currentPage + neighbouringPages
      }
    }
    return range(start, end + 1)
  }, [totalPages, currentPage])

  const pages = pageRange
  const isFirstPage = currentPage === 1
  const isLastPage = currentPage >= totalPages

  return (
    <PaginationContainer>
      {!isFirstPage ? (
        <PaginationButton
          to={
            currentPage === 2
              ? `${pagePrefix}`
              : `${pagePrefix}/${currentPage - 1}`
          }
        >
          <LeftOutlined />
        </PaginationButton>
      ) : (
        <DisabledButton>
          <LeftOutlined />
        </DisabledButton>
      )}
      {pages.map((pageNumber) => (
        <PaginationButton
          key={pageNumber}
          $highlighted={pageNumber === currentPage}
          to={pageNumber === 1 ? `${pagePrefix}` : `${pagePrefix}${pageNumber}`}
        >
          {pageNumber}
        </PaginationButton>
      ))}
      {!isLastPage ? (
        <PaginationButton
          $disabled={isLastPage}
          to={`${pagePrefix}${currentPage + 1}/`}
        >
          <RightOutlined />
        </PaginationButton>
      ) : (
        <DisabledButton>
          <RightOutlined />
        </DisabledButton>
      )}
    </PaginationContainer>
  )
}

export default Pagination
